export const meta = {
  id: '05-basics-2',
  order: 5,
  checkFocus: { cases: ['acc'] },   // what the end-of-lesson check tests first
  titleFi: 'Perusteet 2',
  titleEn: 'Basics 2',
  duolingoUnit: 'duo-03-basics-2',
  lemmaIds: ['rhakitegon', 'gīmigon', 'daor', 'paerī', 'ȳdragon'],
  exampleIds: [
    'ex-295193094c03',
    'ex-3d4f7fb841ca',
    'ex-180e7652a1ae',
    'ex-c9e8ea44acc4',
    'ex-924c429d7a6a',
    'hotd-s1e05-005',
    'ex-0da175c37f8d',
    'ex-c53f25e7a310',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Basics_2',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Nominative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Accusative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Verb_Phrases',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Noun_Declensions#Fifth_Declension',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['present'],
  drills: { cases: ['acc'] },
  showLines: ['hotd-s1e05-005'],
  pronunciation: { focus: 'long-vowels', words: ['ābra', 'iotāptegon', 'sȳz', 'jiōrna'] },
  letter: {
    fi: 'Kirjoita kolme lausetta, joissa joku näkee jotakin (akkusatiivi).',
    en: 'Write three sentences in which someone sees something (accusative).',
  },
  scene: 'hotd-s1e05-1',
}

# Perusteet 2

Tällä oppitunnilla korkeavalyria alkaa näyttää siltä, mitä se on: kieleltä, jossa sanan loppu kertoo, kuka tekee mitä kenellekin. Duolingon osiossa ei ole uusia sanoja; apulauseiden muutama sana on lopussa. Sen sijaan on kaksi isoa asiaa: substantiivin sijat ja verbin päätteet.

## Kaksi sijaa: nominatiivi ja akkusatiivi

Korkeavalyrian substantiivin muoto vaihtelee sen mukaan, mikä sen tehtävä lauseessa on – aivan kuten suomessa. Tässä osiossa sijoja on kaksi. Kaikkiaan niitä on kahdeksan (Kieliopissa <TopicLink id="cases">Sijat</TopicLink>), ja loput opitaan vähitellen <LessonLink n={23}>oppitunnille 23</LessonLink> mennessä.

**Nominatiivi** on subjektin sija. Se on sanan perusmuoto: muoto, jonka opettelette ensimmäisenä ja jonka löydätte sanakirjasta. Kaikki tähän asti oppimanne sanat ovat olleet nominatiivissa. Suomessa on sama: sanakirjan *mies* on nominatiivi.

**Akkusatiivi** on objektin sija. Lauseessa ’mies näkee naisen’ mies on näkijä eli subjekti, joten korkeavalyriassa se on nominatiivissa. Nainen on se, joka nähdään, eli objekti, ja se on akkusatiivissa.

<Example id="ex-295193094c03" />

<Interlinear hv="Val|a ābr|e urne|s." gloss="mies-NOM nainen-ACC nähdä-PRS.3SG" fi="Mies näkee naisen." />

Sanan <Hv glossed>ābra</Hv> ’nainen’ loppu on vaihtunut: *-a* on muuttunut *-e*:ksi. Juuri tämä pääte kertoo, kuka nähdään.

Englannissa tekijän ja kohteen erottaa sanajärjestys: verbi on niiden välissä. Korkeavalyriassa verbi on lopussa, joten järjestys ei voi kertoa samaa. Siksi Duolingon Tips & Notes -ohjeiden mukaan molemmat seuraavat lauseet ovat oikein ja tarkoittavat samaa:

- <Hv>Vala ābre urnes.</Hv>
- <Hv fi="Mies näkee naisen.">Ābre vala urnes.</Hv>

### Tässä suomi auttaa

Suomalainen ymmärtää tämän heti, koska suomessa on sama ilmiö. *Mies näkee naisen* ja *Naisen näkee mies* kertovat saman tapahtuman, koska objektin pääte *-n* pysyy mukana, vaikka järjestys vaihtuu. Englanninpuhujalle tämä vaatii totuttelua, teille se on arkea.

<Compare fi="nais|en" hv="ābr|e" label="objekti yksikössä" />

Kun roolit vaihtuvat, molemmat päätteet vaihtuvat. Tämä esimerkki on menneessä ajassa, mutta sijat toimivat samoin:

<Example id="ex-3d4f7fb841ca" />

Ensimmäisessä lauseessa <Hv>vala</Hv> on subjekti ja <Hv>ābre</Hv> objekti. Toisessa <Hv>vale</Hv> on objekti ja <Hv>ābra</Hv> subjekti.

<Example id="ex-180e7652a1ae" />

Tässä kysymyksessä objekti tulee ensin. Pääte *-e* paljastaa, että mies on se, joka nähdään.

### Tässä suomi ei auta

Suomen objektilla on monta sijaa: *näen naisen*, *en näe naista*, *rakastan naista*. Duolingon ohjeiden ja wikin kieliopin mukaan korkeavalyrian objekti on akkusatiivissa, eikä kielessä ole suomen kokonais- ja osaobjektin kaltaista eroa. Älä siis etsi korkeavalyriasta partitiivia.

## Miten akkusatiivi muodostetaan

Akkusatiivin voi muodostaa monella tavalla, ja kaikki tavat opitaan aikanaan. Tähän osioon riittävät ohjeiden tärkeimmät säännöt:

- **-a → -e**: <Hv>ābra</Hv> → <Seam s="ābr" e="e" />, <Hv>vala</Hv> → <Seam s="val" e="e" />
- **-i → -ī** (monikon nominatiivi): <Hv>vali</Hv> → <Seam s="val" e="ī" />, <Hv>ābri</Hv> → <Seam s="ābr" e="ī" />
- **-es → -ī**: <Hv>hontes</Hv> → <Seam s="hont" e="ī" />
- **-ys → -i**: <Hv>azantys</Hv> → <Seam s="azant" e="i" />
- **-yssy → -ī**: <Hv>azantyssy</Hv> → <Seam s="azant" e="ī" />

<Paradigm lemma="ābra" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="azantys" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="hontes" cases="nom,acc" numbers="sg,pl" />

Huomaa, kuinka usein ero on pelkkä vokaalin pituus: <Hv glossed>vali</Hv> ’miehet’ (subjektina) ja <Hv glossed>valī</Hv> ’miehet’ (objektina). Tässä suomalainen on vahvoilla. Kuulette eron yhtä helposti kuin eron sanojen *tuli* ja *tuuli* välillä. Suomen monikossa nominatiivi ja objekti näyttävät samalta (*naiset näkevät – näen naiset*), mutta korkeavalyriassa ne erottaa juuri pituus.

<Compare fi="naise|t" hv="ābr|ī" label="objekti monikossa" />

<Example id="ex-c9e8ea44acc4" />

Tässä <Hv glossed>hontī</Hv> on yksikön akkusatiivi ’linnun’. <Hv>Hontes</Hv>-sanalla yksikön ja monikon akkusatiivi ovat samat, joten vain asiayhteys kertoo, montako lintua nähtiin.

<Note>
Duolingon ohjeiden sääntö ”-i → -ī” koskee monikon nominatiiveja, jotka päättyvät *-i*:hin, kuten <Hv>vali</Hv>. Sanat, jotka päättyvät *-i*:hin jo yksikössä, ovat eri asia: esimerkiksi <Hv glossed>kēli</Hv> ’kissa’ ja <Hv glossed>brōzi</Hv> ’nimi’ ovat akkusatiivissakin <Hv>kēli</Hv> ja <Hv l="brōzi">brōzi</Hv>. Wikin taivutussivun mukaan tässä <Term id="deklinaatio">taivutusluokassa</Term> nominatiivi ja akkusatiivi ovat yksikössä ja monikossa samat.
</Note>

## Verbin päätteet

Korkeavalyrian verbi mukautuu subjektiinsa persoonassa ja luvussa. Tässä osiossa tulee vastaan kolme muotoa: yksikön ensimmäinen persoona (minä), yksikön kolmas persoona (hän, se) ja monikon kolmas persoona (he).

Ohjeiden nyrkkisäännöt:

- Kun subjekti on ’minä’, verbi päättyy **-n**:ään.
- Kun subjekti on ’hän, se’, päätteessä on **-s** tai **-z**.
- Kun subjekti on ’he’, päätteessä on **-zi**, **-si** tai **-is**.

### Tässä suomi auttaa – yllättävän paljon

Yksikön ensimmäisen persoonan pääte on sama kuin suomessa: *näe-n* ja <Seam s="urne" e="n" />.

<Compare fi="näe|n" hv="urne|n" label="1. persoona, yksikkö" />

<Compare fi="näke|vät" hv="urne|si" label="3. persoona, monikko" />

Kolmannessa persoonassa yhtäläisyys loppuu. Suomen *näkee* ei saa päätettä vaan pidentää vokaalinsa, kun taas korkeavalyriassa pääte on *-s*: <Seam s="urne" e="s" />.

<Paradigm lemma="urnegon" filter="indicative.active.present" />

Taulukossa näkyy koko preesens. Olla-verbin kaikki kuusi persoonaa osaat jo <LessonLink n={3}>oppitunnilta 3</LessonLink>, mutta tavallisten verbien toisen persoonan ja monikon ensimmäisen persoonan muodot opetellaan kunnolla vasta <LessonLink n={14}>oppitunnilla 14</LessonLink>. Kuuntelukorteissa niitä voi tulla vastaan jo nyt. Keskity tällä oppitunnilla kolmeen tuttuun muotoon.

<Example id="ex-924c429d7a6a" />

<Interlinear hv="Jelm|io rȳb|an se hūr|e urne|n." gloss="tuuli-ACC kuulla-PRS.1SG ja kuu-ACC nähdä-PRS.1SG" fi="Kuulen tuulen ja näen kuun." />

Kumpikin verbi päättyy *-n*:ään, ja subjekti ’minä’ jää pois – kuten suomessakin. Sana <Hv glossed>jelmio</Hv> ’tuuli’ näyttää akkusatiivissa samalta kuin nominatiivissa.

<Line id="hotd-s1e05-005" />

Rhaenyran repliikin viimeinen sana <Hv glossed>rȳban</Hv> on ’kuulen’, eli taas *-n*.

### Kolmannen persoonan kirjavuus

Ohjeet neuvovat katsomaan tarkkaan, onko yksikön kolmannen persoonan pääte *-s*, *-sa*, *-za* vai *-as*, koska kaikki verbit eivät taivu samalla tavalla:

- <Seam glossed s="urne" e="s" /> ’näkee’
- <Seam s="majaq" e="sa" /> tai <Seam glossed s="majaq" e="as" /> ’ihailee’
- <Seam glossed s="jorrāel" e="za" /> ’rakastaa’

Monikossa vastaavasti: <Seam glossed s="urne" e="si" /> ’näkevät’, <Seam s="majaq" e="si" /> tai <Seam glossed s="majaq" e="is" /> ’ihailevat’, <Seam glossed s="jorrāel" e="zi" /> ’rakastavat’.

<Example id="ex-0da175c37f8d" />

<Example id="ex-c53f25e7a310" />

Nämä yleistykset riittävät toistaiseksi. Verbien taivutus käydään myöhemmin läpi järjestelmällisesti.

## Apufraasit

Kolme fraasia lisää keskustelun avuksi. Opettele ne kokonaisina: kielto tulee vasta <LessonLink n={17}>oppitunnilla 17</LessonLink> ja imperatiivi <LessonLink n={21}>oppitunnilla 21</LessonLink>.

<Practice hv="Rhakition daor." fi="En ymmärrä." />

<Practice hv="Gīmion daor." fi="En tiedä." />

<Practice hv="Paerī ȳdrās." fi="Puhu hitaasti." />

Kaksi ensimmäistä on muodostettu Petersonin lauseen <Hv>Vestragon koston daor.</Hv> mallilla, kolmas lauseen <Hv>Gevī ȳdrā, azantys!</Hv> mallilla.

## Harjoitellaan

<Practice hv="Ābra vale urnes." fi="Nainen näkee miehen." />

<Practice hv="Vale ābra urnes." fi="Nainen näkee miehen." />

<Practice hv="Vali ābrī urnesi." fi="Miehet näkevät naiset." />

<Practice hv="Azantys hontī urnes." fi="Ritari näkee linnun." />

<Practice hv="Azanti urnen." fi="Näen ritarin." />

Kaksi ensimmäistä lausetta tarkoittavat samaa. Järjestys vaihtuu, mutta päätteet pitävät roolit paikallaan.

### Kuka teki kenelle?

Valitse, kumpi suomennos on oikea. Sen kertovat päätteet, ei sanajärjestys.

<KukaTeki />

<Vocab />

<Check />
